import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, AtSign, Loader2, Sparkles, X } from 'lucide-react';
import { type Agent, SORTIE_AGENT } from '../../hooks/useAgents';
import BoutonDictee from './BoutonDictee';

interface Props {
  agents: Agent[];
  /** Agent désigné par « @ », ou null pour laisser la plateforme choisir. */
  agent: Agent | null;
  onAgent: (agent: Agent | null) => void;
  valeur: string;
  onChange: (valeur: string) => void;
  onEnvoyer: () => void;
  occupe?: boolean;
  placeholder?: string;
  /** Version large, pour la page d'accueil des conversations. */
  large?: boolean;
  autoFocus?: boolean;
  /** Ce qu'on dit de l'agent quand aucun n'est désigné. */
  mentionAuto?: string;
}

const sansAccents = (texte: string) =>
  texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Zone de saisie des conversations.
 *
 * On écrit comme à quelqu'un. Taper « @ » ouvre la liste des agents et permet
 * d'en désigner un ; sans désignation, la plateforme choisit l'agent d'après
 * le message. Le choix reste visible au-dessus de la saisie et se retire d'un
 * clic.
 */
const Composeur: React.FC<Props> = ({
  agents,
  agent,
  onAgent,
  valeur,
  onChange,
  onEnvoyer,
  occupe = false,
  placeholder = 'Écrivez votre message…',
  large = false,
  autoFocus = false,
  mentionAuto = "L'agent adapté sera choisi d'après votre message",
}) => {
  const zone = useRef<HTMLTextAreaElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  // Position du « @ » en cours de saisie dans le texte, ou null.
  const [arobase, setArobase] = useState<number | null>(null);
  const [filtre, setFiltre] = useState('');
  const [surligne, setSurligne] = useState(0);

  const proposes = useMemo(() => {
    if (arobase === null) return [];
    const cle = sansAccents(filtre);
    return agents
      .filter(
        (a) =>
          !cle
          || sansAccents(a.title).includes(cle)
          || sansAccents(a.category_name || '').includes(cle),
      )
      .slice(0, 8);
  }, [agents, arobase, filtre]);

  useEffect(() => setSurligne(0), [filtre, arobase]);

  // Ouverte vers le bas, la liste peut dépasser la fenêtre : on la fait
  // défiler jusqu'à ce qu'elle soit visible en entier.
  const menuOuvert = arobase !== null;
  useEffect(() => {
    if (large && menuOuvert) menu.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [large, menuOuvert]);

  // La zone grandit avec le texte, jusqu'à une hauteur raisonnable.
  useEffect(() => {
    const el = zone.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, large ? 280 : 220)}px`;
  }, [valeur, large]);

  const lireArobase = (texte: string, curseur: number) => {
    const avant = texte.slice(0, curseur);
    const correspondance = avant.match(/(^|\s)@([^\s@]*)$/);
    if (correspondance) {
      setArobase(curseur - correspondance[2].length - 1);
      setFiltre(correspondance[2]);
    } else {
      setArobase(null);
      setFiltre('');
    }
  };

  const choisir = (choisi: Agent) => {
    if (arobase !== null) {
      // On retire « @texte » de la saisie : l'agent s'affiche à part.
      const fin = arobase + 1 + filtre.length;
      const suivant = (valeur.slice(0, arobase) + valeur.slice(fin)).replace(/^\s+/, '');
      onChange(suivant);
    }
    onAgent(choisi);
    setArobase(null);
    setFiltre('');
    requestAnimationFrame(() => zone.current?.focus());
  };

  const ouvrirListe = () => {
    const el = zone.current;
    const curseur = el?.selectionStart ?? valeur.length;
    const prefixe = curseur > 0 && !/\s/.test(valeur[curseur - 1]) ? ' @' : '@';
    const suivant = valeur.slice(0, curseur) + prefixe + valeur.slice(curseur);
    onChange(suivant);
    requestAnimationFrame(() => {
      if (!el) return;
      const position = curseur + prefixe.length;
      el.focus();
      el.setSelectionRange(position, position);
      lireArobase(suivant, position);
    });
  };

  const surTouche = (evenement: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (arobase !== null && proposes.length > 0) {
      if (evenement.key === 'ArrowDown') {
        evenement.preventDefault();
        setSurligne((i) => (i + 1) % proposes.length);
        return;
      }
      if (evenement.key === 'ArrowUp') {
        evenement.preventDefault();
        setSurligne((i) => (i - 1 + proposes.length) % proposes.length);
        return;
      }
      if (evenement.key === 'Enter' || evenement.key === 'Tab') {
        evenement.preventDefault();
        choisir(proposes[surligne]);
        return;
      }
    }
    if (evenement.key === 'Escape' && arobase !== null) {
      evenement.preventDefault();
      setArobase(null);
      return;
    }
    if (evenement.key === 'Backspace' && !valeur && agent) {
      onAgent(null);
      return;
    }
    if (evenement.key === 'Enter' && !evenement.shiftKey) {
      evenement.preventDefault();
      if (valeur.trim() && !occupe) onEnvoyer();
    }
  };

  return (
    <div className="relative">
      {arobase !== null && (
        // Sur l'écran d'arrivée, la saisie est en haut : la liste s'ouvre vers
        // le bas et s'affiche en entier. Dans une conversation, la saisie est
        // collée en bas de l'écran : elle s'ouvre vers le haut.
        <div
          ref={menu}
          className={`absolute left-0 right-0 z-30 overflow-hidden rounded-2xl text-left
            bg-eg-blanc shadow-[0_16px_40px_-12px_rgba(31,45,53,.25)] outline outline-1
            -outline-offset-1 outline-black/[0.08] ${large ? 'top-full mt-2' : 'bottom-full mb-2'}`}
        >
          <div className="border-b border-eg-bordure px-3.5 py-2 text-[11.5px] font-medium text-eg-estompe">
            {proposes.length > 0 ? 'Choisir un agent' : `Aucun agent ne correspond à « ${filtre} »`}
          </div>
          <div className={`${large ? 'max-h-[60vh]' : 'max-h-72'} overflow-y-auto p-1.5`}>
            {proposes.map((propose, index) => (
              <button
                key={propose.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  choisir(propose);
                }}
                onMouseEnter={() => setSurligne(index)}
                className={`flex w-full items-start gap-3 rounded-xl px-2.5 py-2 text-left ${
                  index === surligne ? 'bg-eg-voile' : ''
                }`}
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg
                  bg-eg-accent/[0.12] text-eg-accent">
                  <AtSign className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className="truncate text-[13.5px] font-medium text-eg-encre">
                      {propose.title}
                    </span>
                    <span className="shrink-0 text-[11px] text-eg-estompe">
                      {SORTIE_AGENT[propose.output_format || ''] || ''}
                    </span>
                  </span>
                  {(propose.summary || propose.category_name) && (
                    <span className="mt-0.5 line-clamp-1 block text-[12px] text-eg-gris">
                      {propose.summary || propose.category_name}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className={`rounded-[22px] bg-eg-blanc shadow-carte outline outline-1 -outline-offset-1
          outline-black/[0.09] transition-shadow focus-within:outline-eg-accent/40
          focus-within:shadow-[0_0_0_4px_rgba(108,146,134,.35)] ${large ? 'p-3.5' : 'p-2.5'}`}
      >
        <textarea
          ref={zone}
          value={valeur}
          autoFocus={autoFocus}
          rows={large ? 2 : 1}
          onChange={(e) => {
            onChange(e.target.value);
            lireArobase(e.target.value, e.target.selectionStart);
          }}
          onClick={(e) => lireArobase(valeur, (e.target as HTMLTextAreaElement).selectionStart)}
          onKeyDown={surTouche}
          onBlur={() => setTimeout(() => setArobase(null), 120)}
          placeholder={placeholder}
          className={`block w-full resize-none border-0 bg-transparent px-1.5 text-eg-encre
            placeholder:text-eg-estompe focus:outline-none focus:ring-0 ${
              large ? 'min-h-[56px] text-[15px] leading-relaxed' : 'min-h-[24px] text-[14px] leading-relaxed'
            }`}
        />

        <div className="mt-2 flex items-center gap-2">
          {agent ? (
            <span className="inline-flex max-w-[70%] items-center gap-1.5 rounded-full bg-eg-ombre py-1
              pl-2.5 pr-1.5 text-[12px] font-medium text-white">
              <AtSign className="h-3 w-3 shrink-0 opacity-70" />
              <span className="truncate">{agent.title}</span>
              <button
                type="button"
                onClick={() => onAgent(null)}
                className="rounded-full p-0.5 hover:bg-white/15"
                aria-label="Retirer l'agent"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={ouvrirListe}
              className="inline-flex items-center gap-1.5 rounded-full bg-black/[0.045] px-2.5 py-1
                text-[12px] font-medium text-eg-gris hover:bg-black/[0.07] hover:text-eg-encre"
              title="Désigner un agent"
            >
              <AtSign className="h-3 w-3" />
              Agent
            </button>
          )}
          {!agent && (
            <span className="hidden items-center gap-1 truncate text-[11.5px] text-eg-estompe sm:inline-flex">
              <Sparkles className="h-3 w-3 shrink-0" />
              <span className="truncate">{mentionAuto}</span>
            </span>
          )}

          <span className="ml-auto" />
          <BoutonDictee
            disabled={occupe}
            onTexte={(texte) => {
              onChange(valeur.trim() ? `${valeur.trimEnd()} ${texte}` : texte);
              zone.current?.focus();
            }}
          />
          <button
            type="button"
            onClick={() => valeur.trim() && !occupe && onEnvoyer()}
            disabled={!valeur.trim() || occupe}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full
              bg-eg-ombre text-white transition-all hover:bg-eg-profond disabled:bg-black/[0.12]
              disabled:text-white active:scale-95"
            aria-label="Envoyer"
          >
            {occupe ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" strokeWidth={2.2} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Composeur;
